import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useLocation } from '@remix-run/react';
import { useEffect } from 'react';

import { useAuth } from '~/hooks/useAuth';

const MONITORED_STAGES = ['staging', 'uat', 'prod'];

export function initializeDatadogRum() {
  const VITE_STAGE = import.meta.env.VITE_STAGE;
  const VITE_DDRUM_APP_ID = import.meta.env.VITE_DDRUM_APP_ID;
  const VITE_DDRUM_CLIENT_TOKEN = import.meta.env.VITE_DDRUM_CLIENT_TOKEN;
  const GIT_COMMIT_SHA = import.meta.env.VITE_GIT_COMMIT_SHA;
  const API_GATEWAY_URL = import.meta.env.VITE_API_URL;

  if (MONITORED_STAGES.includes(VITE_STAGE)) {
    datadogRum.init({
      applicationId: VITE_DDRUM_APP_ID,
      clientToken: VITE_DDRUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'enterprise-dashboard',
      env: VITE_STAGE,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: GIT_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      silentMultipleInit: true,
      allowedTracingUrls: [API_GATEWAY_URL],
      enableExperimentalFeatures: ['feature_flags'],
    });

    // Datadog auto-correlates logs with the active RUM session when both SDKs
    // initialize in the same page context. forwardErrorsToLogs/forwardConsoleLogs
    // convert uncaught errors and console.error/warn calls into log entries
    // without requiring changes at each call site.
    //
    // beforeSend is a fallback for that automatic linkage: it isn't guaranteed
    // on every log (e.g. a log fired before RUM has an active session, or RUM
    // blocked client-side while the Logs beacon still gets through), so we
    // read RUM's current session/view directly and stamp it on every log that
    // has one available.
    datadogLogs.init({
      clientToken: VITE_DDRUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'enterprise-dashboard',
      env: VITE_STAGE,
      version: GIT_COMMIT_SHA,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn'],
      sessionSampleRate: 100,
      silentMultipleInit: true,
      beforeSend: (event) => {
        // getInternalContext() is an undocumented internal API, so its failure mode isn't
        // contractually guaranteed. Isolate it so a thrown error can't block the log from
        // being sent — correlation is a best-effort enrichment, not a delivery requirement.
        try {
          const rumContext = datadogRum.getInternalContext();
          if (rumContext?.session_id) {
            event.session_id = rumContext.session_id;
            event.session = { ...event.session, id: rumContext.session_id };
          }
          if (rumContext?.view?.id) {
            event.view = { ...event.view, id: rumContext.view.id };
          }
        } catch {
          // swallow: see comment above
        }
        return true;
      },
    });
  }
}

export const DatadogRum = () => {
  const { user } = useAuth();
  const location = useLocation();
  initializeDatadogRum();

  useEffect(() => {
    if (user?.sub) {
      datadogRum.setUser({
        id: user.sub,
        email: user.email,
        enterprisePartnerId: user.enterprisePartnerId,
        roles: user.roles,
      });
    }
  }, [user]);

  useEffect(() => {
    // One log per navigation (not per render/query-param change) — bounded by how much a
    // user actually clicks around, giving Datadog a real "info" signal beyond errors/warnings
    // without approaching the volume of an interaction-level logger.
    if (MONITORED_STAGES.includes(import.meta.env.VITE_STAGE)) {
      datadogLogs.logger.info('page view', { path: location.pathname });
    }
  }, [location.pathname]);

  return null;
};
